/**
 * THE LINE UNDER A VISIT'S DAY, on Home's next visit, on each of Schedule's cards and on the skip
 * or move sheet: the cleaner and the window, as the artboards draw it, or what ops's status says
 * instead (the seam, section 3). A clean done says "Your home is ready", a clean under way says
 * so, and a visit ops cancelled says it is cancelled, and none of them ever shows a window or a
 * time. A clean under way is "today's" only on its own day, `today` being the business's day as
 * the Worker answers it: Schedule's month draws the days gone by, and ops may leave a clean under
 * way past its day, which is then "this clean". Null when there is nothing whole to say, such as
 * before a cleaner is assigned, so a screen draws no half a line.
 *
 * A DAY BEFORE TODAY IS SAID IN THE PAST, since the month draws a year of them (the final browser
 * check of 2026-10-17): a clean done was done; a visit the cleaner could not get into is said
 * without the promise that the office will be in touch, made on the day; one cancelled was
 * cancelled; and one ops still has planned was due, never that it was done or missed, since ops
 * has not pushed what came of it (binding rule 1). A clean ops still has under way is under way,
 * since that is what ops says of it now. Only the month draws such a day: the List starts at today,
 * Home's next visit is ahead, and the sheet opens only on a visit from today on. On such a day a
 * skip or a move is said only once ops has decided or closed it (`requestSaid`), and Home's card of
 * a clean done before today says so in the past (`dayGone`).
 *
 * WHICH HOME A VISIT IS AT, said only when she may see more than one home (provisional P40): the
 * home's label as ops typed it, under the heading of whatever draws the visit, and nothing at all
 * with one home, so every artboard, each drawn for one home, stays as drawn. Never the address.
 */
import type { Visit } from '../api.ts';
import { arriving, VISIT, wasDue } from '../words.ts';
import { timeOfDay } from './dates.ts';

/**
 * Whether a visit's day is before today, the business's day as the Worker answers it: the one test
 * of the past tense, which the line under a day, a request on its card and Home's clean card each
 * ask. A visit with no day is never gone.
 */
export function dayGone(visit: { readonly date?: string }, today: string): boolean {
  return visit.date !== undefined && visit.date < today;
}

/** The states of a request ops has decided, accepted or declined, or closed, for whatever reason. */
const SETTLED: ReadonlySet<string> = new Set(['accepted', 'declined', 'cancelled']);

/**
 * Whether a card says where a skip or a move about a visit stands, by the request's state. From
 * today on, always. On a day gone by, only once ops has decided it or closed it: accepted or
 * declined, in the office's own words, or closed or withdrawn. Sent, waiting and the lines that ask
 * her to call each promise something still to come about a visit that has passed, and are left off
 * (the replay of the owner's walk, 2026-10-17). Showing less than ops has keeps to binding rule 1.
 */
export function requestSaid(
  visit: { readonly date?: string },
  state: string,
  today: string,
): boolean {
  return !dayGone(visit, today) || SETTLED.has(state);
}

export function visitLine(visit: Visit, today: string): string | null {
  const gone = dayGone(visit, today);
  if (visit.status === 'done') return gone ? VISIT.wasDone : VISIT.done;
  if (visit.status === 'in_progress') {
    return visit.date === today ? VISIT.underWay : VISIT.underWayNotToday;
  }
  if (visit.status === 'no_access') return gone ? VISIT.couldNotGetInThen : VISIT.couldNotGetIn;
  if (visit.status === 'cancelled') return gone ? VISIT.wasCancelled : VISIT.cancelled;
  const start = visit.window ? timeOfDay(visit.window.start) : null;
  const end = visit.window ? timeOfDay(visit.window.end) : null;
  if (visit.cleaner === undefined || start === null || end === null) return null;
  return gone ? wasDue(visit.cleaner, start, end) : arriving(visit.cleaner, start, end);
}

/**
 * The name of the home a visit is at, as ops typed its label, when `homes`, the homes she may see
 * through the scoped read, are more than one; otherwise nothing. Nothing too for a visit with no
 * home, a home not among hers, or a home with no label or a blank one. The one place this rule
 * lives: every screen that draws a visit asks it.
 */
export function homeNameOf(
  visit: { readonly homeId?: string },
  homes: readonly { readonly id: string; readonly label?: string }[],
): string | undefined {
  if (homes.length < 2 || visit.homeId === undefined) return undefined;
  const label = homes.find((home) => home.id === visit.homeId)?.label;
  return label === undefined || label.trim() === '' ? undefined : label;
}
